// ════════════════════════════════════════════════════════════════
// FloorCost — Router (pure)
// ────────────────────────────────────────────────────────────────
// Pure routing decisions extracted from src/boot.js so tests can
// run in plain Node without jsdom. Mirrors the validation.js
// extraction pattern.
// ════════════════════════════════════════════════════════════════

export const APP_PREFIX = '/app';
export const CALLBACK_PATH = '/auth/callback';
export const SIGN_IN_PATH = '/';

/**
 * Decide which surface the boot router should render for a given
 * pathname + session state.
 *
 *   /auth/callback           → 'callback' (parse hash, then redirect)
 *   /app or /app/*  signed   → 'app'
 *   /app or /app/*  signed-out → 'redirect-signin'
 *   anything else   signed   → 'redirect-app'   (signed-in operators
 *                                                 don't dwell on `/`)
 *   anything else   signed-out → 'signin'
 *
 * @param {string} pathname
 * @param {boolean} signedIn
 * @returns {'signin' | 'app' | 'callback' | 'redirect-app' | 'redirect-signin'}
 */
export function pickRoute(pathname, signedIn) {
  if (pathname === CALLBACK_PATH || pathname.startsWith(CALLBACK_PATH + '/')) {
    return 'callback';
  }
  const wantsApp = pathname === APP_PREFIX || pathname.startsWith(APP_PREFIX + '/');
  if (wantsApp) {
    return signedIn ? 'app' : 'redirect-signin';
  }
  return signedIn ? 'redirect-app' : 'signin';
}

// ── In-app routes (R2-1) ─────────────────────────────────────────
// The app tier owns the tab manifest (rail-items.js); lib stays the
// leaf, so the known-tab list is an argument rather than an import.

/**
 * Canonical path for an in-app tab: `/app/carpet`, `/app/tracker`, …
 * @param {string} tab
 * @returns {string}
 */
export function pathForTab(tab) {
  return APP_PREFIX + '/' + tab;
}

/**
 * Extract the tab a pathname deep-links to.
 *
 *   /app                → null   (bare app root — caller keeps its default pane)
 *   /app/tracker        → 'tracker'
 *   /app/tracker/xyz    → 'tracker'  (deeper segments reserved for later
 *                                     R2 slices, e.g. /app/tracker/:id)
 *   /app/nope           → null   (unknown first segment)
 *   /anything-else      → null   (not an app path — boot's pickRoute owns it)
 *
 * @param {string} pathname
 * @param {readonly string[]} knownTabs - valid tab ids (from the rail manifest)
 * @returns {string | null}
 */
export function parseAppTab(pathname, knownTabs) {
  if (pathname !== APP_PREFIX && !pathname.startsWith(APP_PREFIX + '/')) return null;
  const rest = pathname.slice(APP_PREFIX.length + 1); // '' for bare /app
  const first = rest.split('/', 1)[0];
  if (!first) return null;
  return knownTabs.includes(first) ? first : null;
}
